import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { shrink } from '../offline/photos.js';
import PhotoButtons from './PhotoButtons.jsx';

// Optional photos in a pop-up: take or choose up to `max`, shrunk on the
// phone, with previews and a remove button. `photos` is [{ blob, url }];
// the caller queues the blobs when the form is sent.
// `stamp` (the job reference) is printed on each photo with the date and time.
export default function PhotoPicker({ photos, setPhotos, max = 6, label = 'Photos', optional = true, onBusy, stamp = null }) {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  // Free the previews when the pop-up closes.
  const shown = useRef([]);
  shown.current = photos;
  useEffect(() => () => shown.current.forEach((p) => URL.revokeObjectURL(p.url)), []);
  useEffect(() => { onBusy?.(adding); }, [adding, onBusy]);

  const add = async (picked) => {
    const files = picked.slice(0, max - photos.length);
    if (!files.length) return;
    setAdding(true);
    setError(null);
    try {
      const out = [];
      for (const f of files) {
        const blob = await shrink(f, { label: stamp });
        out.push({ blob, url: URL.createObjectURL(blob) });
      }
      setPhotos((p) => [...p, ...out]);
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };
  const remove = (i) => setPhotos((p) => {
    URL.revokeObjectURL(p[i].url);
    return p.filter((_, j) => j !== i);
  });

  return (
    <div className="popup-field">
      <span>{label} {optional && <span className="muted">(optional)</span>}</span>
      {photos.length > 0 && (
        <ul className="photo-picks">
          {photos.map((p, i) => (
            <li key={p.url}>
              <img src={p.url} alt={`Photo ${i + 1}`} />
              <button type="button" className="photo-remove" aria-label={`Remove photo ${i + 1}`} onClick={() => remove(i)}><X size={14} aria-hidden="true" /></button>
            </li>
          ))}
        </ul>
      )}
      {photos.length < max && <PhotoButtons onFiles={add} multiple disabled={adding} title={label} />}
      {adding && <span className="small muted">Adding photo…</span>}
      {error && <span className="popup-hint">{error}</span>}
    </div>
  );
}
